// Every place in D-ORCA the assistant may send someone — the one list.
//
// THE CATALOG IS THE ONLY DEFINITION OF A DESTINATION. The backend offers from
// it, the frontend builds hrefs from it, and the pages read their params with
// the vocabulary it declares. Trusstor's version kept the same facts in five
// places — a Pydantic literal, a docstring, a prompt table, TS interfaces and a
// filter hook — and a `floors` filter was declared in one and applied in none.
//
// PARAM NAMES ARE THE PAGES' OWN. `item`, `doc`, `delivery`, `focusTask` are what
// each page already reads from its URL; the catalog adopts them rather than
// inventing a vocabulary a page would have to translate.
//
// WHAT MAY BE HERE (spec ruling 7): real, site-scoped pages. A page still served
// from fixtures is never a destination — sending someone to mock data as though
// it answered their question is an invented observation. Neither is a gated
// page, until the gate can be enforced where the offer is made.
//
// A STORED LINK OUTLIVES THIS FILE'S CURRENT SHAPE. Conversations live 90 days,
// and links are stored with them as intent (spec ruling 2). Removing a
// destination or a param is safe — the reader drops what no longer parses.
// RENAMING a param is not: it silently turns every stored link that used it
// into a link without it.

import { flag, oneOf, someOf, token, type ParamSpecs, type ParamsOf } from './params.js';

export interface Destination<P extends ParamSpecs = ParamSpecs> {
  /**
   * The route. `:siteId` is filled from the conversation's own site; any other
   * `:name?` segment is a param of the same name, and must be optional.
   */
  path: string;
  params: P;
  /** English, for the model: what this page is for. Every destination may be offered with nothing filtered. */
  purpose: string;
  /** Valid params, iterated by the codec round-trip test and the page contract tests. */
  examples: readonly ParamsOf<P>[];
}

function defineDestination<const P extends ParamSpecs>(destination: {
  path: string;
  params: P;
  purpose: string;
  examples: readonly ParamsOf<NoInfer<P>>[];
}): Destination<P> {
  return destination;
}

/**
 * The statuses the Activities & Issues list filters on, in its own order:
 * issue statuses, activity statuses, then the shared `completed`.
 *
 * Literals rather than an import. The issue half lives in `trusstor/` and the
 * activity half in `client/`, which server code may not import — so each side
 * pins this list against its own enum in a test instead, and a status added
 * upstream fails a test rather than becoming silently unlinkable.
 */
export const ACTIVITY_LIST_STATUSES = [
  'planned',
  'forReview',
  'rejected',
  'blocked',
  'done',
  'completed',
] as const;

export const DESTINATIONS = {
  dashboard: defineDestination({
    path: '/dashboard/:siteId',
    params: {},
    purpose: "the site's dashboard: today at a glance — schedule, safety, workforce, deliveries, weather",
    examples: [{}],
  }),
  monitor: defineDestination({
    path: '/monitor/:siteId',
    params: { floor: token() },
    purpose: 'the live monitor: who and what is on site right now, floor by floor',
    examples: [{}, { floor: 'floor-3' }],
  }),
  activities: defineDestination({
    path: '/activities/:siteId',
    params: {
      view: oneOf(['list', 'tracker']),
      types: someOf(['activity', 'issue']),
      status: oneOf(ACTIVITY_LIST_STATUSES),
      critical: flag(),
      openActivity: token(),
      openIssue: token(),
      // Retired `act:<id>` / `iss:<id>`, kept so links already stored in past
      // answers still render with it; the page rewrites it to the two above.
      item: token(),
    },
    purpose: 'Activities & Issues: the daily list of reported work and issues, and the progress tracker',
    examples: [
      {},
      { view: 'list', types: ['issue'], status: 'planned' },
      { view: 'list', types: ['issue'], critical: true },
      { view: 'list', openIssue: '66e1f0c2a1b2c3d4e5f60718' },
      { openActivity: 't-1' },
    ],
  }),
  scheduler: defineDestination({
    path: '/scheduler/:siteId/:scheduleNumber?',
    params: { scheduleNumber: token(), focusTask: token(), openTask: token() },
    purpose: 'the scheduler: the Gantt chart of the imported programme',
    examples: [{}, { scheduleNumber: 'KT-0001' }, { scheduleNumber: 'KT-0001', focusTask: 't-1', openTask: 't-1' }],
  }),
  documents: defineDestination({
    path: '/documents/:siteId',
    params: { folder: token(), doc: token() },
    purpose: 'documents: drawings and files, their versions and approvals',
    examples: [{}, { folder: 'folder-1', doc: 'file-1' }],
  }),
  logistics: defineDestination({
    path: '/logistics/:siteId',
    // `stock` opens one material's stock drawer, by its name — the stock
    // panel's own key for a material.
    params: { delivery: token(), stock: token() },
    purpose: 'logistics: the delivery board and material stock',
    examples: [{}, { delivery: 'DEL-041' }, { stock: 'Rebar D16' }],
  }),
  analytics: defineDestination({
    path: '/analytics/:siteId',
    params: {},
    purpose: 'analytics: workforce reports — contractor analysis, timesheets, daily routes, incidents',
    examples: [{}],
  }),
  productivity: defineDestination({
    path: '/productivity/:siteId',
    params: {},
    purpose: 'productivity: planned against actual work over time',
    examples: [{}],
  }),
  equipment: defineDestination({
    path: '/equipment/:siteId',
    params: {},
    purpose: 'equipment: the tracked machines, such as hoists and lifts, and where they are',
    examples: [{}],
  }),
};

export type DestinationId = keyof typeof DESTINATIONS;

/** A destination's params, typed. */
export type DestinationParams<D extends DestinationId> = ParamsOf<(typeof DESTINATIONS)[D]['params']>;

export const DESTINATION_IDS = Object.keys(DESTINATIONS) as DestinationId[];

export function isDestinationId(value: unknown): value is DestinationId {
  return typeof value === 'string' && Object.hasOwn(DESTINATIONS, value);
}
